import React from 'react';
import BlogArticle from 'components/blog/blogArticle';
import { Post } from 'components/blog/content/styled';
import { authorKalina as author } from 'data/authors';
import img from 'img/blog/coveruxcoronavirus.png';

import img1 from 'img/blog/remote.png';
import img2 from 'img/blog/piramida.png';
import img3 from 'img/blog/beargrylls.png';
import img4 from 'img/blog/sessionreplaysnewpageLS.png';
import img5 from 'img/blog/getthecorona.png';
import img6 from 'img/blog/lby.png';

const Content = () => {
  return (
    <Post>
      <p>
        You’ve read a lot about uncertain times and social distancing. We’re all surrounded by the
        same words, but what exactly do they mean for the UX people?
      </p>
      <p>
        The nearest future is just the tip of the iceberg. The COVID-19 pandemic is likely to change
        the way we work for the years to come.
      </p>
      <p>
        It’s a challenge we barely expected, a challenge that hit us hard, but we can still learn
        something from it. The job of a UX professional is about finding solutions, after all.
      </p>
      <p>In this article, we’re going to talk about:</p>
      <ul>
        <li>remote work culture in the UX team</li>
        <li>remote UX research when you can’t talk to your users</li>
        <li>content strategy and UX writing in times of crisis</li>
        <li>expected changes in the UX industry</li>
      </ul>
      <p>We’re in this together. Let’s dive in – while keeping a safe distance, of course.</p>
      <br />
      <ul>
        <li>
          <p>
            <a href="#remote-teams">Remote teams</a>
          </p>
          <ul>
            <li>
              <a href="#micromanagement">The issue with micromanagement</a>
            </li>
            <li>
              <a href="#psychological-safety">Psychological safety</a>
            </li>
            <li>
              <a href="#remote-workshops">Running remote workshops</a>
            </li>
          </ul>
        </li>
        <li>
          <p>
            <a href="#remote-ux">Remote UX research</a>
          </p>
          <ul>
            <li>
              <a href="#user-testing-alternatives">Alternatives to user testing</a>
            </li>
          </ul>
        </li>
        <li>
          <p>
            <a href="#content-design">Content design</a>
          </p>
          <ul>
            <li>
              <a href="#clarity">Strive for clarity</a>
            </li>
            <li>
              <a href="#right-tone">Use the right tone</a>
            </li>
          </ul>
        </li>
        <li>
          <p>
            <a href="#ux-industry">Changes in the UX industry</a>
          </p>
          <ul>
            <li>
              <a href="#growth-of-agile">The growth of agile methodologies</a>
            </li>
            <li>
              <a href="#empathy">Empathy in use</a>
            </li>
          </ul>
        </li>
      </ul>
      <aside className="blue">
        <h2>We’re here to help</h2>
        <p>
          LiveSession is now <b>50% off</b> for everyone affected by COVID-19. If you’re a{' '}
          <b>non-profit organization</b>, you can get <b>LiveSession for free</b>.
        </p>
        <p>
          Sounds helpful?{' '}
          <b>
            <u>
              <a
                href="https://hello811074.typeform.com/to/hN0CeL"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#000' }}
              >
                Get in touch
              </a>
            </u>{' '}
            for more details
          </b>
          .
        </p>
      </aside>

      <h2 id="remote-teams">Remote teams</h2>

      <p>
        <img src={img1} alt="Illustration of remote work." title="Remote work" />
      </p>
      <p>
        UX professionals are herd creatures. We work best when we work together during meetings,
        brainstorming sessions and—above all—workshops. In most cases, these group activities
        usually require all participants to be in the same room.
      </p>
      <p>
        Unfortunately, getting together no longer possible. We’re all staying home now. Whether we
        like it or not, we need to switch the boardroom for a conference call app. You already know
        that there a lot of technical difficulties to tackle, from poor internet connection to{' '}
        <a
          href="https://fortune.com/2020/04/02/zoom-bombing-what-is-meeting-hacked-how-to-prevent-vulnerability-is-zoom-safe-video-chats/"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          Zoom bombing
        </a>{' '}
        incidents. The key issues, however, don’t come with the technicals – they come with trust.
      </p>
      <h3 id="micromanagement">The issue with micromanagement</h3>

      <p>
        Even if we try very hard, we’re losing some things that come with face-to-face
        communication. This is the point where one of the most common issues emerges:{' '}
        <em>micromanagement</em>. You can’t see how your colleagues are working. Instead, you need
        to take their word for it.
      </p>
      <p>
        This situation is a tough test for many UX team leaders. You can get too relaxed and lose
        track of your goals, or go to the other extreme and become a control freak.
      </p>
      <p>
        The second version happens to be more common. Even if this wasn’t a thing before, managers
        start to ask remote workers to submit to-do lists, detailed plans, and daily updates.
        There’s a very fine line between staying up to date and breathing down someone’s neck, even
        if you’re kilometers away.
      </p>
      <p>
        There is no single magical cure, yet there is one concept that can help you cope with this
        new situation.
      </p>
      <h3 id="psychological-safety">Psychological safety</h3>

      <p>
        <a
          href="https://rework.withgoogle.com/guides/understanding-team-effectiveness/steps/introduction/"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          Google’s Project Oxygen research
        </a>{' '}
        is a goldmine of interesting findings. The people behind it have examined what makes a
        successful team.
      </p>
      <p>So, what happens to be the key factor?</p>
      <p>
        What might be surprising to some, it’s not about salaries, seniority, or fancy offices.{' '}
        <strong>Psychological safety</strong> turned out to be the most important quality.
      </p>
      <p>
        Think about it this way: we already have to deal with an unprecedented, uncertain world
        situation. No one really needs extra pressure from the work environment. It’s as basic as
        Maslow’s famous hierarchy of needs. You can’t do your best if you don’t feel safe.
      </p>
      <p>
        <img
          src={img2}
          alt="Maslow&#39;s hierarchy of needs, going from the tip: self-actualization, esteem, love/belonging, safety (emphasized), and physiological."
          title="Maslow&#39;s pyramid"
        />
      </p>
      <p>
        Amy Edmondson of Harvard University, the person behind the concept, defines{' '}
        <a
          href="https://www.jstor.org/stable/2666999?seq=1"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          psychological safety
        </a>{' '}
        as “a shared belief held by members of a team that the team is safe for interpersonal
        risk-taking”. In other words, a team member should feel encouraged to try innovative
        approaches without worrying that this will put them in a tough spot. Research from Google
        suggests that giving employees opportunities to use their unique talents will boost their
        effectiveness, which will ultimately result in more revenue.
      </p>
      <p>
        Here are some tips based on Project Oxygen’s findings. These will help you foster
        psychological safety in your own team:
      </p>
      <ul>
        <li>
          Talk openly about your <strong>personal work style preferences</strong>. We all have
          different personalities and it’s OK to have different habits. Encourage your team to do
          the same.
        </li>
        <li>
          <strong>Clarify the responsibilities</strong> of every team member. Do this at the start
          of a remote project (or a sprint) so that everyone knows their scope. This will help them
          develop a sense of ownership.
        </li>
        <li>
          <strong>Emphasize the impact</strong> of everyone’s work. When the employees work their
          work matters, their motivation will skyrocket. Who needs micromanagement when you work
          with people who feel responsible?
        </li>
      </ul>
      <p>
        This approach can help you emerge successfully from the crisis. We’ve covered the core
        values, now it’s time to move on to getting things done.
      </p>
      <h3 id="remote-workshops">Running remote workshops</h3>

      <p>
        I get it, running remote workshops may not be your preferred method. As a UX designer, or a
        researcher, you’ve already learned to appreciate the power of face-to-face meetings.
      </p>
      <p>Nevertheless, we need to adapt.</p>
      <p>
        <img
          src={img3}
          alt="Bear Grylls with a caption: Improvise. Adapt. Overcome."
          title="Bear Grylls"
        />
      </p>
      <p>
        Without further ado, here are our best tips to improve your remote workshop game right away:
      </p>
      <ul>
        <li>
          <strong>Turn your camera on</strong>, at least for a little bit. The internet connection
          isn’t always perfect, as we all know, but it’s always good to see the faces of the
          participants. You don’t have to stare at each other for two hours, yet a little smile and
          a wave at the start creates a better sense of connection.
        </li>
        <li>
          I<strong>nclude alone time</strong>. This will give your participants space to think,
          sketch, and create. Another good tip comes from{' '}
          <a
            href="https://miro.com/blog/100-remote-workshops-learnings/"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            Shipra Kayan
          </a>
          , a freelance consultant for remote teams. She suggests <em>playing music</em> during
          breaks for sketching or brainstorming. This little trick helps maintain a sense of unity,
          even when you’re apart.
        </li>
        <li>
          <strong>Use a tool to visualize your ideas</strong>. Most people don’t have a whiteboard
          at home and they need to look for a digital equivalent. At LiveSession, we’re really happy
          using{' '}
          <a href="https://whimsical.com/" target="_blank" rel="noopener noreferrer nofollow">
            Whimsical
          </a>
          .
        </li>
      </ul>
      <p>Feeling a bit more confident now? Let’s move on to remote UX research.</p>
      <h2 id="remote-ux">Remote UX research</h2>

      <p>
        Just like with on-site work, on-site research became impossible. At the beginning of March
        2020, companies started{' '}
        <a
          href="https://uxdesign.cc/we-cancelled-our-user-research-because-of-the-coronavirus-f39b33c9cfdf"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          canceling face-to-face user tests
        </a>{' '}
        to protect both the participants and the researchers. What was a dilemma back then is now a
        certainty. No one doubts that we need to conduct all sessions remotely.
      </p>
      <p>
        And here’s where another issue comes in. Yes, you can run your research remotely, but what
        about finding participants? People are scared about their jobs in the first place. Barely
        anyone has the mental and physical capacity to participate in voluntary research. Even if
        they would genuinely like to help, they’ve got more things to worry about.
      </p>
      <p>
        So, how do you deal with this? Are there any other effective ways to get insights without
        involving your users?
      </p>
      <h3 id="user-testing-alternatives">Alternatives to user testing</h3>

      <p>
        You might want to look into the possibilities that come with{' '}
        <strong>session replays</strong>. We’ll be completely honest: you won’t be able to ask
        additional questions and talk to the user directly. Despite that, session recordings can
        still serve as a source of valuable qualitative data, especially when there is no better
        alternative available.
      </p>
      <p>
        <img src={img4} alt="Illustration of session replays." title="LiveSession replay session" />
      </p>
      <p>
        Sessions recordings happen in the background. Most of the time, the users don’t even realize
        that they’re being recorded. This means that you don’t need to ask them for a favor, and
        there are more advantages to it as well.<strong>The users are not under pressure</strong>{' '}
        and they behave more naturally. The research results are not influenced by any{' '}
        <a href="/blog/cognitive-bias-ux/" target="_blank" rel="noopener noreferrer">
          cognitive biases
        </a>{' '}
        or group pressure.
      </p>
      <p>
        Sounds like a good alternative for your needs? If you’d like to dig deeper, you’re likely to
        enjoy our in-depth{' '}
        <a href="/blog/guide-to-qualitative-ux-research/" target="_blank" rel="noopener noreferrer">
          guide to qualitative user research
        </a>
        .
      </p>
      <h2 id="content-design">Content design</h2>

      <p>You’ve probably seen this screenshot before:</p>
      <p>
        <img
          src={img5}
          alt="A dialog box asking for coronavirus updates. Two options to choose from: Yes, I want coronavirus and Dismiss."
          title="Popup with coronavirus updates"
        />
      </p>
      <p>
        As absurd as it seems, it raises an important issue. Coronavirus is an entirely new thing
        and no one has taught us how to write about it. We need to figure it out ourselves. Here’s
        how you can get it right:
      </p>
      <h3 id="clarity">Strive for clarity</h3>

      <p>
        Yes, you should stick to plain language at all times, yet it’s particularly important during
        the pandemic. People don’t want to face ambiguities when they’re in danger. They look for{' '}
        <strong>clear rules and plain expressions</strong>. In this case, it’s best to stick to
        communication rules coming from an expert on the subject, the{' '}
        <a
          href="https://www.who.int/about/communications/understandable/plain-language"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          World Health Organization
        </a>
        :
      </p>
      <ul>
        <li>
          <strong>Get straight to the point</strong> – It’s not the right time for elaborate
          statements. Your users are overwhelmed and they won’t process more information than
          needed.
        </li>
        <li>
          <strong>Be clear about the goal</strong> – Describe the desired behavior and the outcome.
          Instead of encouraging social distancing, which is quite unclear, tell your audience to
          stay home to protect themselves.
        </li>
        <li>
          <strong>Choose familiar words</strong> – Avoid complicated expressions, such as{' '}
          <em>precautionary measures</em>. Keep things simple and talk about{' '}
          <em>actions to reduce risk</em> instead. You can find other good examples in this{' '}
          <a
            href="https://docs.google.com/document/d/1G3hswLekmqyV7zGD5l2Lv80L91A8_6_fWxdt2WBOxnA/edit?_hsenc=p2ANqtz-_V6vTyqKU7v-C6tOeH5g4136-tEL9fh95wjFu_yjO-L3-X9dcJOWfWMLD2AVK06pWUHwDrqmali5l6HvMkKWOT4D8_hQ&amp;_hsmi=85118288&amp;fbclid=IwAR2oW_g2Hn6OYRZQ_ne94bb0N2eXVvUvZrw2GKbQgo3JfLW3nOWeVKl-lpo#heading=h.8r9x0dbcymb"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            coronavirus plain language guide
          </a>
          , created by content strategist Lizzie Bruce.
        </li>
      </ul>
      <h3 id="right-tone">Use the right tone</h3>

      <p>
        Once the language is clear, there’s one more thing to consider: the tone. It’s not the right
        time to be salesy, pushy, and overly enthusiastic. It’s the right time to make use of your
        empathy.
      </p>
      <p>
        Use this opportunity to let your users know that your care about their safety. Make it clear
        that you’re here to help them out. If you need inspiration, you can find it on this{' '}
        <a
          href="https://pl.pinterest.com/awenzh/good-job-microcopy/coronavirus-related-en/"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          Pinterest board
        </a>{' '}
        with good practices.
      </p>
      <h2 id="ux-industry">Changes in the UX industry</h2>

      <p>
        <img
          src={img6}
          alt="Illustration of the shift from waterfall to agile methods."
          title="Two persons and changes"
        />
      </p>
      <p>
        Alright, what about the safety of the industry? What’s going to happen to UX professionals?
      </p>
      <p>
        Obviously, we don’t know for sure. The situation is changing dynamically. Our current
        predictions may become outdated in a couple of days. There are, however, some trends that
        seem quite apparent:
      </p>
      <h3 id="growth-of-agile">The growth of agile methodologies</h3>

      <p>
        The{' '}
        <a
          href="https://www.shipbob.com/blog/ecommerce-covid-19/"
          target="_blank"
          rel="noopener noreferrer"
        >
          COVID-19
        </a>{' '}
        pandemic seems like the ultimate decline of the waterfall approach. Imagine defining all the
        requirements, the action plan, and then facing a crisis as serious as the coronavirus
        outbreak. You already know what that means: all your hard work goes to waste.
      </p>
      <p>
        Agile methodologies, on the other hand, are on the rise more than ever before. They allow
        companies to deal with the crisis in a more efficient way. When you work in short sprints,
        it’s much easier to adapt and change your strategy depending on the situation.
      </p>
      <p>
        What’s more, agile is empowering. It puts trust in its team members. With this approach,
        they can make decisions based on their expertise rather than wait for the manager’s
        permission. And yes, it goes in line with psychological safety we’ve mentioned before.
      </p>
      <h3 id="empathy">Empathy in use</h3>

      <p>
        Last but not least, a crisis calls for a UX professional’s core value:{' '}
        <strong>empathy</strong>. As we stand in the face of coronavirus, we talk about common good
        more than we did before. We see that people are anxious and we can use our expertise not
        only to help them achieve their goal but also to comfort them.
      </p>
      <p>
        It’s time to drop judgment and help our users reclaim the sense of control. If you work on
        data science and analytics solutions, you’re the one responsible. This{' '}
        <a
          href="https://www.nngroup.com/videos/ux-and-covid-19/"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          video from Jakob Nielsen
        </a>{' '}
        can give you some ideas.
      </p>
      <p>I hope this article will be a source of inspiration for you. Stay safe!</p>
    </Post>
  );
};

const Wrapped = BlogArticle(Content);

export const frontmatter = {
  title: 'UX in the Time of Coronavirus',
  url: '/blog/ux-in-the-time-of-coronavirus/',
  description: `How is the UX industry dealing with the COVID-19 pandemic? Read
  our insights and predictions.`,
  author,
  img,
  imgSocial: img,
  date: '2020-04-22',
  category: '',
  group: 'Analytics',
  timeToRead: 9,
};

export default () => (
  <Wrapped
    title={frontmatter.title}
    category={frontmatter.category}
    description={frontmatter.description}
    date={frontmatter.date}
    author={frontmatter.author}
    url={frontmatter.url}
    img={frontmatter.img}
    imgSocial={process.env.GATSBY_HOST + frontmatter.img}
    group={frontmatter.group}
    timeToRead={frontmatter.timeToRead}
  />
);
